import {
    ChoiceGroup,
    IChoiceGroupOption,
    DefaultButton,
    Dropdown,
    IDropdownOption,
    IModalStyles,
    Modal,
    PrimaryButton,
    Separator,
    Slider,
    Spinner,
    SpinnerSize,
    Text,
    TextField,
    MessageBar,
    MessageBarType,
    useTheme
} from "@fluentui/react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    cloneRsoConstraintLevels,
    createDefaultRsoConstraintLevels,
    getRsoConstraintLevelEntries,
    IRsoConstraintLevels,
    IRsoConfigurationPreset,
    RSO_CONSTRAINT_LEVEL_FIELDS,
    RSO_CONSTRAINT_LEVEL_LABELS,
    RsoConfigurationSource
} from "../../interfaces/rsoConfiguration";
import { useRsoConfiguration } from "../../providers/rsoConfigurationContext";

const modalMaxHeight = '92vh';

const modalSurfaceStyles: Partial<IModalStyles> = {
    main: {
        width: 'min(1400px, calc(100vw - 24px))',
        maxWidth: 'min(1400px, calc(100vw - 24px))',
        maxHeight: modalMaxHeight,
        minHeight: 'auto',
        overflow: 'hidden',
        borderRadius: 16,
        background: 'transparent',
        boxShadow: '0 24px 60px rgba(15, 23, 42, 0.18)'
    },
    scrollableContent: {
        overflowY: 'hidden',
        maxHeight: modalMaxHeight
    }
};

const modalStyles: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    maxHeight: modalMaxHeight,
    overflow: 'hidden',
    background: 'linear-gradient(180deg, #ffffff 0%, #f7f9fc 100%)'
};

const bodyStyles: React.CSSProperties = {
    flex: '1 1 auto',
    minHeight: 0,
    display: 'grid',
    gridTemplateColumns: '320px minmax(0, 1fr)',
    gap: 20,
    maxHeight: 'calc(92vh - 104px)',
    overflow: 'auto',
    padding: 24
};

const panelStyles: React.CSSProperties = {
    background: '#ffffff',
    border: '1px solid #d9e2ec',
    borderRadius: 14,
    boxShadow: '0 10px 24px rgba(15, 23, 42, 0.04)'
};

const fieldCardStyles: React.CSSProperties = {
    border: '1px solid #e2e8f0',
    borderRadius: 12,
    padding: 12,
    background: 'linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)'
};

const sourceOptions: IChoiceGroupOption[] = [
    { key: 'default', text: 'Default' },
    { key: 'saved', text: 'Saved Custom' },
    { key: 'manual', text: 'Manual for This Run' }
];

const ensurePresetName = (name: string, fallback: string) => {
    const trimmed = String(name ?? '').trim();
    return trimmed.length > 0 ? trimmed : fallback;
};

const RsoConfigurationModal = () => {
    const theme = useTheme();
    const {
        defaultLevels,
        savedPresets,
        selectedPresetId,
        manualOverrideLevels,
        activeLevels,
        activeSource,
        activePresetLabel,
        isEditorOpen,
        isBusy,
        loadError,
        openRsoConfiguration,
        closeRsoConfiguration,
        setSelectedPresetId,
        setManualOverrideLevels,
        clearManualOverrideLevels,
        savePreset,
        deletePreset
    } = useRsoConfiguration();
    const accentColor = theme.palette.themePrimary;
    const accentDark = theme.palette.themeDarkAlt;
    const accentBackground = theme.palette.themeLighterAlt;
    const accentBorder = theme.palette.themeLight;
    const accentText = theme.palette.themeDarker;

    const [source, setSource] = useState<RsoConfigurationSource>('default');
    const [selectedSavedPresetId, setSelectedSavedPresetId] = useState<string | null>(null);
    const [draftName, setDraftName] = useState('Custom RSO Preset');
    const [draftDescription, setDraftDescription] = useState('');
    const [draftLevels, setDraftLevels] = useState<IRsoConstraintLevels>(createDefaultRsoConstraintLevels());
    const [isSavingCustom, setIsSavingCustom] = useState(false);

    const savedPresetOptions: IDropdownOption[] = useMemo(() => {
        return savedPresets.map((preset) => ({
            key: preset.id,
            text: preset.name,
            data: preset
        }));
    }, [savedPresets]);

    const selectedPreset = useMemo(() => {
        if (!selectedSavedPresetId) return null;
        return savedPresets.find((preset) => preset.id === selectedSavedPresetId) ?? null;
    }, [savedPresets, selectedSavedPresetId]);

    const resetFromSource = useCallback((nextSource: RsoConfigurationSource, preset?: IRsoConfigurationPreset | null) => {
        if (nextSource === 'saved') {
            const resolvedPreset = preset ?? selectedPreset ?? savedPresets[0] ?? null;
            if (!resolvedPreset) {
                setSource('default');
                setSelectedSavedPresetId(null);
                setDraftName('Custom RSO Preset');
                setDraftDescription('');
                setDraftLevels(cloneRsoConstraintLevels(defaultLevels));
                return;
            }
            setSource('saved');
            setSelectedSavedPresetId(resolvedPreset.id);
            setDraftName(ensurePresetName(resolvedPreset.name, 'Custom RSO Preset'));
            setDraftDescription(resolvedPreset.description ?? '');
            setDraftLevels(cloneRsoConstraintLevels(resolvedPreset.levels));
            return;
        }

        if (nextSource === 'manual') {
            setSource('manual');
            setSelectedSavedPresetId(null);
            setDraftName('Manual RSO Preset');
            setDraftDescription('');
            setDraftLevels(cloneRsoConstraintLevels(manualOverrideLevels ?? activeLevels));
            return;
        }

        setSource('default');
        setSelectedSavedPresetId(null);
        setDraftName('Custom RSO Preset');
        setDraftDescription('');
        setDraftLevels(cloneRsoConstraintLevels(defaultLevels));
    }, [activeLevels, defaultLevels, manualOverrideLevels, savedPresets, selectedPreset]);

    useEffect(() => {
        if (!isEditorOpen) return;
        const nextSource = activeSource;
        const preset = nextSource === 'saved'
            ? savedPresets.find((item) => item.id === selectedPresetId) ?? savedPresets[0] ?? null
            : null;
        resetFromSource(nextSource, preset);
    }, [activeSource, isEditorOpen, resetFromSource, selectedPresetId]);

    const updateLevel = (key: keyof IRsoConstraintLevels, value?: number | [number, number]) => {
        const rawValue = Array.isArray(value) ? value[0] : value;
        const nextValue = Math.max(0, Math.min(10, Math.round(Number(rawValue ?? 5))));
        setDraftLevels((prev) => ({
            ...prev,
            [key]: Number.isFinite(nextValue) ? nextValue : prev[key]
        }));
    };

    const handleSourceChange = (_: React.FormEvent<HTMLElement | HTMLInputElement> | undefined, option?: IChoiceGroupOption) => {
        const next = (option?.key ?? 'default') as RsoConfigurationSource;
        if (next === 'saved' && savedPresets.length < 1) {
            setSource('default');
            return;
        }
        resetFromSource(next);
    };

    const handleSavedPresetChange = (_: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
        const preset = savedPresets.find((item) => item.id === String(option?.key ?? '').trim()) ?? null;
        resetFromSource('saved', preset);
    };

    const handleSaveChanges = async (saveAsCustom = false) => {
        if (isSavingCustom || isBusy) return;
        setIsSavingCustom(true);
        try {
            const saved = await savePreset({
                id: saveAsCustom ? undefined : selectedSavedPresetId ?? undefined,
                name: draftName,
                description: draftDescription,
                levels: draftLevels
            });
            if (!saved) {
                return;
            }
        } finally {
            setIsSavingCustom(false);
        }
    };

    const handleUseForRun = () => {
        setManualOverrideLevels(draftLevels);
        closeRsoConfiguration();
    };

    const handleUseDefault = () => {
        setSelectedPresetId(null);
        clearManualOverrideLevels();
        closeRsoConfiguration();
    };

    const handleUseSaved = () => {
        if (!selectedSavedPresetId) return;
        setSelectedPresetId(selectedSavedPresetId);
        clearManualOverrideLevels();
        closeRsoConfiguration();
    };

    const handleDelete = async () => {
        if (!selectedSavedPresetId) return;
        await deletePreset(selectedSavedPresetId);
    };

    const renderSlider = (field: keyof IRsoConstraintLevels) => {
        const label = RSO_CONSTRAINT_LEVEL_LABELS[field];
        return (
            <div key={field} style={fieldCardStyles}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>{label}</Text>
                    <span style={{
                        minWidth: 28,
                        textAlign: 'center',
                        borderRadius: 999,
                        padding: '2px 8px',
                        background: accentBackground,
                        color: accentText,
                        border: `1px solid ${accentBorder}`,
                        fontWeight: 700,
                        fontSize: 12
                    }}>
                        {draftLevels[field]}
                    </span>
                </div>
                <Slider
                    min={0}
                    max={10}
                    step={1}
                    snapToStep
                    value={draftLevels[field]}
                    showValue={false}
                    styles={{
                        slideBox: {
                            padding: '4px 0'
                        },
                        lineContainer: {
                            height: 4
                        },
                        activeSection: {
                            background: accentColor,
                            height: 4
                        },
                        inactiveSection: {
                            background: theme.palette.neutralLight,
                            height: 4
                        },
                        thumb: {
                            width: 18,
                            height: 18,
                            border: `2px solid ${accentColor}`,
                            background: theme.palette.white
                        }
                    }}
                    onChange={(value, range) => updateLevel(field, range ?? value)}
                />
            </div>
        );
    };

    const actionBar = () => {
        if (source === 'saved') {
            return (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'flex-end' }}>
                    <DefaultButton onClick={handleUseSaved} disabled={isBusy || isSavingCustom || !selectedSavedPresetId}>
                        Use Selected
                    </DefaultButton>
                    <PrimaryButton onClick={() => void handleSaveChanges(false)} disabled={isBusy || isSavingCustom || !selectedSavedPresetId}>
                        Save Changes
                    </PrimaryButton>
                    <DefaultButton onClick={() => void handleSaveChanges(true)} disabled={isBusy || isSavingCustom}>
                        Save as Custom
                    </DefaultButton>
                    <DefaultButton onClick={() => void handleDelete()} disabled={isBusy || isSavingCustom || !selectedSavedPresetId}>
                        Delete Custom
                    </DefaultButton>
                </div>
            );
        }

        if (source === 'manual') {
            return (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'flex-end' }}>
                    <DefaultButton onClick={() => void handleSaveChanges(true)} disabled={isBusy || isSavingCustom}>
                        Save as Custom
                    </DefaultButton>
                    <PrimaryButton onClick={handleUseForRun} disabled={isBusy || isSavingCustom}>
                        Use for This Run
                    </PrimaryButton>
                    <DefaultButton onClick={handleUseDefault} disabled={isBusy || isSavingCustom}>
                        Reset to defaults
                    </DefaultButton>
                </div>
            );
        }

        return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'flex-end' }}>
                <DefaultButton onClick={() => void handleSaveChanges(true)} disabled={isBusy || isSavingCustom}>
                    Save as Custom
                </DefaultButton>
                <PrimaryButton onClick={handleUseDefault} disabled={isBusy || isSavingCustom}>
                    Reset to defaults
                </PrimaryButton>
                <DefaultButton onClick={handleUseForRun} disabled={isBusy || isSavingCustom}>
                    Use for This Run
                </DefaultButton>
            </div>
        );
    };

    return (
        <Modal
            isOpen={isEditorOpen}
            onDismiss={closeRsoConfiguration}
            isBlocking={isBusy || isSavingCustom}
            styles={modalSurfaceStyles}
        >
            <div style={modalStyles}>
                <div style={{
                    padding: '18px 24px 14px',
                    borderBottom: '1px solid #e5eaf2',
                    background: `linear-gradient(180deg, ${accentColor} 0%, ${accentDark} 100%)`,
                    color: '#fff'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'start' }}>
                        <div>
                            <Text variant="xxLarge" styles={{ root: { color: '#fff', fontWeight: 700 } }}>
                                Configure RSO
                            </Text>
                            <div style={{ marginTop: 4, fontSize: 13, color: '#cbd5e1' }}>
                                Active source: {activePresetLabel}
                            </div>
                        </div>
                        <div style={{ textAlign: 'right', fontSize: 12, color: '#cbd5e1' }}>
                            {isBusy || isSavingCustom ? 'Saving changes...' : 'Edit, save, or use the current configuration for one run.'}
                        </div>
                    </div>
                </div>

                <div style={bodyStyles}>
                    <div style={{ ...panelStyles, padding: 16 }}>
                        <Text variant="large" styles={{ root: { fontWeight: 700 } }}>Source</Text>
                        <div style={{ marginTop: 10 }}>
                            <ChoiceGroup
                                selectedKey={source}
                                options={sourceOptions}
                                onChange={handleSourceChange}
                            />
                        </div>

                        <Separator styles={{ root: { margin: '16px 0' } }} />

                        {source === 'saved' && (
                            <div style={{ display: 'grid', gap: 12 }}>
                                <Dropdown
                                    label="Saved custom preset"
                                    selectedKey={selectedSavedPresetId ?? undefined}
                                    options={savedPresetOptions}
                                    onChange={handleSavedPresetChange}
                                    placeholder={savedPresetOptions.length > 0 ? 'Choose a custom preset' : 'No saved presets yet'}
                                    disabled={savedPresetOptions.length === 0}
                                />
                                <TextField
                                    label="Preset name"
                                    value={draftName}
                                    onChange={(_, value) => setDraftName(value ?? '')}
                                />
                                <TextField
                                    label="Description"
                                    value={draftDescription}
                                    onChange={(_, value) => setDraftDescription(value ?? '')}
                                    multiline
                                    rows={3}
                                />
                            </div>
                        )}

                        {source !== 'saved' && (
                            <div style={{ display: 'grid', gap: 12 }}>
                                <TextField
                                    label="Preset name"
                                    value={draftName}
                                    onChange={(_, value) => setDraftName(value ?? '')}
                                />
                                <TextField
                                    label="Description"
                                    value={draftDescription}
                                    onChange={(_, value) => setDraftDescription(value ?? '')}
                                    multiline
                                    rows={3}
                                />
                            </div>
                        )}

                        <div style={{ marginTop: 16, padding: 12, borderRadius: 12, background: accentBackground, border: `1px solid ${accentBorder}` }}>
                            <Text variant="small" styles={{ root: { color: accentText } }}>
                                Default goals come from the active AvantCare configuration. Manual selections are kept only in-memory until you run or save them.
                            </Text>
                        </div>

                        {loadError && (
                            <div style={{ marginTop: 12 }}>
                                <MessageBar messageBarType={MessageBarType.warning} isMultiline>
                                    {loadError}
                                </MessageBar>
                            </div>
                        )}
                    </div>

                    <div style={{ ...panelStyles, padding: 16 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                            <div>
                                <Text variant="large" styles={{ root: { fontWeight: 700 } }}>Optimisation goals</Text>
                                <div style={{ marginTop: 4, fontSize: 12, color: '#52606d' }}>
                                    0 means lowest priority, 10 means highest priority.
                                </div>
                            </div>
                            {isBusy && <Spinner size={SpinnerSize.small} />}
                        </div>

                        <div style={{
                            marginTop: 16,
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                            gap: 12
                        }}>
                            {RSO_CONSTRAINT_LEVEL_FIELDS.map((field) => renderSlider(field))}
                        </div>

                        <div style={{ marginTop: 18, padding: 12, borderRadius: 12, background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                            <Text variant="smallPlus" styles={{ root: { fontWeight: 600, color: '#334155' } }}>
                                Current goal values
                            </Text>
                            <div style={{ marginTop: 8, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 8 }}>
                                {getRsoConstraintLevelEntries(draftLevels).map((entry) => (
                                    <div key={entry.key} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12, color: '#475569' }}>
                                        <span>{entry.label}</span>
                                        <strong>{entry.value}</strong>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginTop: 18 }}>
                            <div style={{ fontSize: 12, color: '#64748b' }}>
                                Active selection will be shared between the board-level and shift-level optimization entry points.
                            </div>
                            {actionBar()}
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export { RsoConfigurationModal };
